import { Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from '@heroui/react'
import { getOptimalProfilePictureUrl, useProfilePictureDisplay } from '@qse/api'
import { useLogout } from '@qse/auth-ui'
import { type User, UserRoleEnum } from '@qse/constants'
import { AppRoutesList } from '@qse/constants'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import { UserAvatar } from '../components/UserAvatar'
import { LogoutIcon } from '../icons'

/**
 * Truncates text to a specified length and adds ellipsis
 * @param text - The text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text with ellipsis if needed
 */
const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) {
    return text
  }

  return text.substring(0, maxLength) + '...'
}

/**
 * ProfileDropdown component displays the user's profile info and actions.
 * Shows first/last name, tenant name, and dropdown with profile and logout actions.
 * @param user - The current user object
 * @param onLogout - Optional logout handler
 */
export const ProfileDropdown: React.FC<{
  user: User
  onLogout?: () => void
}> = ({ user, onLogout }) => {
  const { forceLogout } = useLogout()
  const navigate = useNavigate()

  // Use lightweight profile picture hook - disable for SUPER_ADMIN
  const { profilePicture } = useProfilePictureDisplay({
    enabled:
      !!user &&
      user.userRole !== UserRoleEnum.SUPER_ADMIN &&
      user.userRole !== UserRoleEnum.RESELLER_ADMIN,
    tenantStatus: user.tenant?.status,
  })

  // Get optimized profile picture URL for small components
  const profilePictureUrl = getOptimalProfilePictureUrl(profilePicture, 'small')

  const handleLogout = () => {
    if (onLogout) {
      onLogout()
    } else {
      forceLogout()
    }
  }

  // Create full name and truncate if needed
  const fullName = `${user.firstName} ${user.lastName}`
  const truncatedName = truncateText(fullName, 30) // Limit to 30 characters for display

  // Create user object with profile picture URL for avatar display
  const userWithProfilePicture = {
    ...user,
    imageUrl: profilePictureUrl || user.imageUrl,
  }

  return (
    <Dropdown
      placement="bottom-end"
      classNames={{
        content:
          'bg-white dark:bg-[#343537] border border-gray-300 dark:border-[#707070] min-w-[320px] max-w-[360px]',
      }}
    >
      <DropdownTrigger>
        <div className="bg-gray-100 dark:bg-[#F1F4F933] py-[10px] px-5 rounded-xl hover:bg-gray-200 dark:hover:bg-[#F1F4F944] transition-all duration-300 cursor-pointer flex gap-3 items-center justify-between min-w-[220px] ">
          <div className="flex flex-col min-w-0 flex-1">
            <h2 className="text-gray-900 dark:text-white text-lg">{truncatedName}</h2>
            {user.tenant?.name && (
              <p className="text-gray-600 dark:text-[#FFFFFFB2] text-xs truncate font-semibold">
                {user.tenant?.name}
              </p>
            )}
          </div>
          <UserAvatar size="md" user={userWithProfilePicture} />
        </div>
      </DropdownTrigger>
      <DropdownMenu
        aria-label="User Actions"
        className="min-w-[320px] max-w-[360px]"
        variant="flat"
      >
        <DropdownItem
          key="profile"
          showDivider
          className="py-3"
          onClick={() => navigate(AppRoutesList.profile)}
        >
          <div className="flex gap-3 items-center">
            <UserAvatar size="md" user={userWithProfilePicture} />
            <div className="min-w-0 flex-1">
              <p className="font-bold text-gray-900 dark:text-white break-words">
                {user.firstName} {user.lastName}
              </p>
            </div>
          </div>
        </DropdownItem>
        <DropdownItem
          key="logout"
          className="text-gray-900 dark:text-white font-medium py-3"
          color="danger"
          startContent={<LogoutIcon />}
          onPress={handleLogout}
        >
          Logout
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  )
}
